import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import Router, { useRouter } from 'next/router';
import * as qs from 'query-string';
import * as PropTypes from 'prop-types';

import { fetchDetails } from '@store/profile/actions';
import { logout } from '@store/auth/actions';
import { apiUrls } from '@constants/api';
import { cureCardsTypes } from '@constants/common';
import routes from '@constants/routes';
import LoaderWrapper from '@components/shared/LoaderWrapper';
import Avatar from '@components/shared/Avatar';
import Title from '@components/shared/Title';
import Link from '@components/shared/Link';
import ActionIcon from '@components/shared/ActionIcon';
import ProfileInfo from '@components/Profile/Info';
import ReviewCard from '@components/Reviews/Card';
import SuccessStory from '@components/shared/SuccessStory';
import Cure from '@components/Cure/Cure';
import ReviewsEmptyState from '@components/Reviews/EmptyState';
import Helpers from '@utils/Helpers';
import Tabs from '@components/shared/Tabs/Tabs';
import Pagination from '@components/shared/Pagination';
import Logger from '@utils/Logger';
import API from '@utils/API';
import Loader from '@components/shared/Loader';

const tabKeys = {
  reviews: 'reviews',
  combinations: 'combinations',
  recipes: 'recipes'
};

const listPageSize = 12;

const ProfilePage = ({
  isPublicProfile,
  loading,
  details,
  fetchDetails,
  currentUser,
  logout
}) => {
  const tabs = [
    {
      value: tabKeys.reviews,
      fetch: {
        url: isPublicProfile
          ? apiUrls.activities.reviews.list
          : apiUrls.activities.reviews.my
      },
      title: 'Reviews'
    },
    {
      value: tabKeys.combinations,
      fetch: {
        url: apiUrls.combinations.list
      },
      title: 'Success stories'
    },
    {
      value: tabKeys.recipes,
      fetch: {
        url: apiUrls.cures.list,
        params: {
          type: 'recipes'
        }
      },
      title: 'Recipes'
    }
  ];

  const getRouterQueryTab = () => {
    if (!router.query.tab) {
      return 0;
    }
    if (router.query.tab >= tabs.length) {
      return 0;
    }
    return Number(router.query.tab);
  };

  const router = useRouter();
  const routerQueryTab = getRouterQueryTab();

  const [selectedTab, selectTab] = useState(routerQueryTab);
  const [page, setPage] = useState(Number(router.query.page) || 1);
  const [list, setList] = useState([]);
  const [listIsLoading, setListIsLoading] = useState(false);
  const [counters, setCounters] = useState({});

  const setFiltersToUrl = filters => {
    const queryString = qs.stringify(filters);
    Router.push(
      {
        pathname: isPublicProfile
          ? routes.profile.public.pattern
          : routes.profile.my,
        search: queryString
      },
      isPublicProfile
        ? `${routes.profile.public.path(router.query.id)}?${queryString}`
        : `${routes.profile.my}?${queryString}`
    );
  };

  let userId = get(currentUser, 'id');

  if (isPublicProfile) {
    userId = router.query.id;
  }

  const fetchList = async () => {
    const currentTab = tabs[selectedTab];
    try {
      setListIsLoading(true);
      const response = await API.get(currentTab.fetch.url, {
        ...currentTab.fetch.params,
        created_by: userId,
        page_size: listPageSize,
        page
      });
      setList(response.data.results);
    } catch (e) {
      Logger.error(e);
    } finally {
      setListIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch profile details
    fetchDetails({ id: userId });
  }, []);

  useEffect(() => {
    setCounters({
      [tabKeys.reviews]: details.reviews_count,
      [tabKeys.combinations]: details.success_stories_count,
      [tabKeys.recipes]: details.recipes_count
    });
  }, [details]);

  useEffect(() => {
    if ('page' in router.query) {
      setPage(Number(router.query.page));
    }
    if ('tab' in router.query) {
      const tabFromQuery = routerQueryTab;

      if (tabFromQuery !== selectedTab) {
        setList([]);
        selectTab(tabFromQuery);
      }
    }
  }, [router.asPath]);

  useEffect(() => {
    fetchList();

    setFiltersToUrl({
      tab: selectedTab,
      page
    });
  }, [page, selectedTab]);

  let favoritesCount = Helpers.counterLimiter(
    get(currentUser, 'favourites_count', 0)
  );

  const handleTypeChange = index => {
    if (index !== selectedTab) {
      setList([]);
      setPage(1);
      selectTab(index);
    }
  };

  const handleChangePagination = ({ page }) => {
    setPage(page);
  };

  const avatar = get(details, 'system_avatar.file');

  const renderList = () => {
    const currentTab = tabs[selectedTab];
    const currentTabTitle = currentTab.title.toLowerCase();

    if (!listIsLoading && !list.length) {
      return (
        <ReviewsEmptyState
          size="sm"
          title={
            isPublicProfile
              ? `${details.username} hasn't added any ${currentTabTitle} yet`
              : `You haven't added any ${currentTabTitle} yet`
          }
          caption={
            isPublicProfile
              ? null
              : 'Add reviews, success stories, or recipes to help people who share the same challenges!'
          }
        />
      );
    }

    switch (currentTab.value) {
      case tabKeys.reviews:
        return (
          <div className="row">
            {list.map(review => (
              <div key={review.id} className="col-xs-12 col-md-12">
                <ReviewCard
                  onRemoved={() => {
                    // Update list and counter in profile store - can be more effective, timeout for backend removing lag
                    setCounters({
                      ...counters,
                      [tabKeys.reviews]: counters[tabKeys.reviews] - 1
                    });
                    setTimeout(() => {
                      fetchList();
                    }, 250);
                  }}
                  compact
                  review={review}
                />
              </div>
            ))}
          </div>
        );
      case tabKeys.combinations:
        return (
          <div className="row">
            {list.map(story => (
              <div
                key={story.id}
                className="col-xs-12 col-md-6 profile-page__story"
              >
                <SuccessStory story={story} author={story.created_by} />
              </div>
            ))}
          </div>
        );
      case tabKeys.recipes:
        return (
          <div className="row">
            {list.map(recipe => (
              <div key={recipe.id} className="profile-page__recipe">
                <Cure cure={recipe} type={cureCardsTypes.horizontal} />
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <LoaderWrapper isLoading={loading}>
      <section className="profile-page">
        <div className="container">
          <div className="profile-page__header">
            <div className="row no-margin jc-between-xxs ai-middle-xxs">
              <div className="row no-margin">
                <div className="profile-page__user ai-top-xxs">
                  <Avatar className="profile-page__avatar" src={avatar} />
                  <div>
                    <Title className="profile-page__title" bold size="lg">
                      {details.username}
                    </Title>
                    {!isPublicProfile && (
                      <Link
                        className="profile-page__favorites-btn"
                        href={routes.profile.favorites}
                      >
                        <ActionIcon type="favorite" isActive />
                        <div className="profile-page__favorites-caption">
                          Favorites ({favoritesCount})
                        </div>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
              {!isPublicProfile && (
                <div className="profile-page__controls">
                  <Link href={routes.profile.settings} bold>
                    Settings
                  </Link>
                  <Link bold onClick={logout}>
                    Logout
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-xxs-12 col-md-4">
              <ProfileInfo details={details} isEditable={!isPublicProfile} />
            </div>
            <div className="col-xxs-12 col-md-8">
              <Tabs
                id="profile-page-tabs"
                disabled={listIsLoading}
                className="profile-page__tabs"
                selectedIndex={selectedTab}
                tabs={tabs}
                type="line"
                onSelect={handleTypeChange}
                getTabTitle={({ title, value }) => (
                  <span>
                    {title}{' '}
                    {!!counters[value] && (
                      <span className="profile-page__tabs-counter">
                        ({counters[value]})
                      </span>
                    )}
                  </span>
                )}
              />
              {listIsLoading ? (
                <Loader />
              ) : (
                <div className="profile-page__data" key={selectedTab}>
                  {renderList()}
                  <Pagination
                    className="profile-page__pagination"
                    key={page}
                    pagination={{
                      count: counters[tabs[selectedTab].value],
                      page: page,
                      page_size: listPageSize
                    }}
                    disabled={loading}
                    onChange={handleChangePagination}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </LoaderWrapper>
  );
};

ProfilePage.propTypes = {
  isPublicProfile: PropTypes.bool,
  loading: PropTypes.bool,
  currentUser: PropTypes.any,
  details: PropTypes.any,
  fetchDetails: PropTypes.func,
  fetchCounters: PropTypes.func,
  logout: PropTypes.func
};

const mst = ({ auth: { user }, profile: { details, loading } }) => ({
  currentUser: user,
  details,
  loading
});

export default connect(mst, {
  fetchDetails,
  logout
})(ProfilePage);
